import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { APP_ENVIRONMENT } from '../../../../../chat-common/src/lib/services/environment.service';
import { UserService } from '../../../../../chat-common/src/lib/services/user.service';

/**
 * Talks to the WhatsApp microservice (`whatsapp-ms`).
 *
 * Authentication is handled by the standard HTTP interceptors, which attach the
 * Bellabeat access token like they do for every other `*.api.bellabeat.io`
 * service — no special-casing needed here.
 *
 * It also owns the "is WhatsApp connected" state, so both the settings list and
 * the connect flow read the same source instead of each keeping its own copy.
 */
@Injectable({ providedIn: 'root' })
export class WhatsappService {

  private readonly baseUrl: string;

  private readonly connectedNumberSubject = new BehaviorSubject<string | null>(null);

  /** Emits the connected number, or `null` while no number is connected. */
  readonly connectedNumber$ = this.connectedNumberSubject.asObservable();

  constructor(
    private http: HttpClient,
    private userService: UserService,
    @Inject(APP_ENVIRONMENT) private environment: any,
  ) {
    this.baseUrl = this.environment.whatsappBaseUrl;
    this.restoreConnectedNumber();
  }

  get connectedNumber(): string | null {
    return this.connectedNumberSubject.value;
  }

  get isConnected(): boolean {
    return !!this.connectedNumberSubject.value;
  }

  /**
   * Publishes the connected number, or clears it when passed `null`.
   *
   * No backend "get status" endpoint exists yet, so the last confirmed number is
   * cached locally (per user) to keep the UI in sync across reloads.
   */
  setConnectedNumber(number: string | null): void {
    if (number) {
      localStorage.setItem(this.storageKey(), number);
    } else {
      localStorage.removeItem(this.storageKey());
    }
    this.connectedNumberSubject.next(number);
  }

  /** Re-reads the cached number, e.g. after the user finishes signing in. */
  restoreConnectedNumber(): void {
    this.connectedNumberSubject.next(localStorage.getItem(this.storageKey()));
  }

  private storageKey(): string {
    const id = this.userService.user?.id || 'anon';
    return `whatsapp:number:${id}`;
  }

  /** Registers a phone number and triggers a confirmation code over WhatsApp. */
  registerNumber(number: string): Observable<any> {
    const url = `${this.baseUrl}/api/user-number/register-number?number=${encodeURIComponent(number)}`;
    return this.http.post(url, null);
  }

  /** Confirms the pending number with the code the user received. */
  confirmNumber(code: string): Observable<any> {
    const url = `${this.baseUrl}/api/user-number/confirm-number?code=${encodeURIComponent(code)}`;
    return this.http.post(url, null);
  }

  /** Re-sends the confirmation code for a number still awaiting confirmation. */
  resendConfirmationCode(): Observable<any> {
    const url = `${this.baseUrl}/api/user-number/resend-confirmation-code`;
    return this.http.post(url, null);
  }

  /** Disconnects the currently registered number. */
  deleteNumber(): Observable<any> {
    const url = `${this.baseUrl}/api/user-number/delete-number`;
    return this.http.delete(url);
  }
}
